import { Box } from '@mui/material';
import { useWatch } from 'react-hook-form';

import { Select } from '@/components/Form/Select';
import { TextInput } from '@/components/Form/TextInput';

import { FUNDING_SOURCE_MAX_LENGTH } from '@/constants/places';
import { getFundingSourceOptions } from '@/constants/user';

import { useAppSelector } from '@/hooks/redux';
import { useTranslate } from '@/hooks/useTranslate';

import { selectUserTenant } from '@/redux/user-data/selectors';

import { FundingSource } from '@/types/user';

import { QuestionProps } from '../QuestionDrawer.interface';
import { useRemediationAccountForm } from './hook/useRemediationAccountForm';
import {
  AccountSectionFormProps,
  RemediationAccountQuestionForm,
} from './RemediationAccountQuestionForm';

import { FormFieldContainer } from '../QuestionAboutMultipleMonthlyDeposit.styled';

type AccountFields = { fundingSource: string; otherFundingSource: string };

export function RemediationQuestionFundingSources({
  onAnswered,
  ...props
}: Readonly<QuestionProps>) {
  const { t } = useTranslate();

  const formState = useRemediationAccountForm<AccountFields>({
    defaultAccountValues: { fundingSource: '', otherFundingSource: '' },
    toDepositInfo: (_account, fields) => ({
      fundingSource:
        fields.fundingSource === FundingSource.OtherIncome
          ? fields.otherFundingSource
          : fields.fundingSource,
    }),
    onFinished: onAnswered,
  });

  return (
    <RemediationAccountQuestionForm
      {...props}
      {...formState}
      submitLabel={t('questionDrawer.remediationFundingSources.button.proceed')}
      AccountSectionForm={FundingSourceSectionForm}
    />
  );
}

function FundingSourceSectionForm({
  index,
  control,
  fieldErrors,
  isPending,
}: AccountSectionFormProps<AccountFields>) {
  const { t } = useTranslate();
  const userTenant = useAppSelector(selectUserTenant);
  const fundingSourceOptions = getFundingSourceOptions(userTenant);

  const watchedAccounts = useWatch({ control, name: 'accounts' });
  const selectedSource = watchedAccounts?.[index]?.fundingSource;

  return (
    <>
      <FormFieldContainer>
        <Select
          options={fundingSourceOptions}
          control={control}
          name={`accounts.${index}.fundingSource`}
          error={!!fieldErrors.accounts?.[index]?.fundingSource}
          helperText={fieldErrors.accounts?.[index]?.fundingSource?.message}
          rules={{
            required: t(
              'questionDrawer.remediationFundingSources.fundingSource.required',
            ),
          }}
          fullWidth
          placeholder={t(
            'questionDrawer.remediationFundingSources.fundingSource.placeholder',
          )}
          useDrawerOnMobile
          disabled={isPending}
          sx={{
            '.MuiOutlinedInput-root': {
              backgroundColor: 'common.white',
            },
          }}
        />
      </FormFieldContainer>

      {selectedSource === FundingSource.OtherIncome && (
        <FormFieldContainer>
          <Box mt={1}>
            <TextInput
              control={control}
              name={`accounts.${index}.otherFundingSource`}
              error={!!fieldErrors.accounts?.[index]?.otherFundingSource}
              helperText={
                fieldErrors.accounts?.[index]?.otherFundingSource?.message
              }
              rules={{
                required: t(
                  'questionDrawer.remediationFundingSources.otherFundingSource.required',
                ),
                maxLength: {
                  value: FUNDING_SOURCE_MAX_LENGTH.otherFundingSource,
                  message: t(
                    'questionDrawer.remediationFundingSources.otherFundingSource.maxLength',
                    {
                      number: FUNDING_SOURCE_MAX_LENGTH.otherFundingSource,
                    },
                  ),
                },
              }}
              placeholder={t(
                'questionDrawer.remediationFundingSources.otherFundingSource.placeholder',
              )}
              disabled={isPending}
            />
          </Box>
        </FormFieldContainer>
      )}
    </>
  );
}
